import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { callApiSecure, httpMethods } from '@caplend/cognito-auth';
import Layout from '../../components/Layout';
import Cognito from '../../auth/cognito';
import * as transactionsActions from '../../store/actions/transactions';
import SendMoneyTransferProgressStep from '../SendMoney/components/SendMoneyTransferProgressStep';

function TransactionProgress() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId: urlRequestId } = useParams();
  const transactionData = location.state?.transaction || location.state || null;
  const fromPath = typeof location.state?.from === 'string' ? location.state.from : '/transaction-history';

  const { requestId: reduxRequestId, crossBorderQuote } = useSelector((state) => state.crossBorder);
  const requestId = urlRequestId || reduxRequestId;

  const [fetchedQuote, setFetchedQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { transactions } = useSelector((state) => state.transactions || {});
  const { currencies: configCurrencies } = useSelector((state) => state.config || {});

  const rawStepsToDisplay = fetchedQuote?.steps || crossBorderQuote?.steps || [];

  const stepsToDisplay = (rawStepsToDisplay || []).map((step, index) => {
    if (index !== 0 || !step?.description || typeof step.description !== 'string') return step;
    const cleanedDescription = step.description
      .replace(/\s*<FlagIcon[^>]*\/>\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return { ...step, description: cleanedDescription };
  });

  useEffect(() => {
    if (urlRequestId) {
      const fetchQuoteData = async () => {
        setLoading(true);
        setError(null);
        try {
          const fxApiUrl = process.env.REACT_APP_FX_API_URL;
          const data = await callApiSecure(`${fxApiUrl}cross-border/quote/execute/${urlRequestId}`, httpMethods.get, Cognito);
          setFetchedQuote(data);
        } catch (err) {
          setError(err);
        } finally {
          setLoading(false);
        }
      };

      fetchQuoteData();
    }
  }, [urlRequestId]);

  useEffect(() => {
    if (!transactionData && !urlRequestId) {
      navigate('/transaction-history');
    }
  }, [transactionData, urlRequestId, navigate]);

  useEffect(() => {
    if (urlRequestId) {
      dispatch(transactionsActions.getTransactions());
    }
  }, [dispatch, urlRequestId]);

  const handleBackHome = () => {
    navigate(fromPath);
  };

  const resolveFirstStepFlagCurrencyCode = () => {
    if (transactionData?.receiveCurrency) return transactionData.receiveCurrency;
    const txs = Array.isArray(transactions) ? transactions : Object.values(transactions || {});
    const tx = txs.find((t) => t.requestId === requestId || t.requestId?.startsWith(requestId));
    if (tx?.quoteCurrency) return tx.quoteCurrency;
    return null;
  };

  const parseLocation = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split(',').map((part) => Number.parseFloat(part.trim()));
    if (parts.length !== 2 || !parts.every((n) => Number.isFinite(n))) return null;
    const [lat, lng] = parts;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return [lat, lng];
  };

  const findCountryByCode = (code) => {
    if (!code || !Array.isArray(configCurrencies)) return null;
    const normalized = String(code).toUpperCase();
    let match = null;
    configCurrencies.some((currency) => {
      if (currency?.country?.code && String(currency.country.code).toUpperCase() === normalized) {
        match = currency.country;
        return true;
      }
      if (Array.isArray(currency?.countries)) {
        const nested = currency.countries.find((c) => c?.code && String(c.code).toUpperCase() === normalized);
        if (nested) {
          match = nested;
          return true;
        }
      }
      return false;
    });
    return match;
  };

  const resolveDestinationCountry = () => {
    const directCode = transactionData?.destinationCountryCode || transactionData?.recipientCountryCode || transactionData?.countryCode;
    if (directCode) {
      const country = findCountryByCode(directCode);
      if (country) return country;
    }

    const currencyCode = resolveFirstStepFlagCurrencyCode();
    if (currencyCode && Array.isArray(configCurrencies)) {
      const normalized = String(currencyCode).toUpperCase();
      const currency = configCurrencies.find((c) => c.code === normalized);
      if (currency?.country) return currency.country;
      if (Array.isArray(currency?.countries) && currency.countries.length > 0) return currency.countries[0];
    }

    return null;
  };

  const destinationCountry = resolveDestinationCountry();
  const destinationCountryCode = destinationCountry?.code ? String(destinationCountry.code).toUpperCase() : null;
  const destinationCoords = parseLocation(destinationCountry?.location);
  const showProgressCard = !!destinationCoords;

  const resolveTransactionFromStore = () => {
    const txs = Array.isArray(transactions) ? transactions : Object.values(transactions || {});
    return txs.find((t) => t.requestId === requestId || t.requestId?.startsWith(requestId));
  };

  const resolveRecipientName = () => {
    if (transactionData?.recipientName) return transactionData.recipientName;
    const tx = resolveTransactionFromStore();
    return tx?.recipientName || tx?.beneficiaryName || null;
  };

  const recipientNameForGlobe = resolveRecipientName();
  const tx = resolveTransactionFromStore();

  if (loading) return null;

  if (error) {
    const errorTitle = error.title || error.message || 'Failed to fetch transaction data';
    const errorDetail = error.detail || error.message || 'An unexpected error occurred';
    const errorStatus = error.status || '';

    return (
      <Layout>
        <div className='flex-1 p-6'>
          <div className='space-y-6'>
            <div>
              <h2 className='text-3xl font-bold tracking-tight'>Transaction Progress</h2>
              <p className='text-muted-foreground'>Unable to load transaction details</p>
            </div>
            <div className='rounded-lg border border-red-200 bg-red-50 p-4'>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0'>
                  <svg className='h-5 w-5 text-red-400' viewBox='0 0 20 20' fill='currentColor'>
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='flex-1'>
                  <h3 className='text-sm font-medium text-red-800'>
                    {errorStatus && `${errorStatus} - `}
                    {errorTitle}
                  </h3>
                  <p className='mt-2 text-sm text-red-700'>{errorDetail}</p>
                  {requestId && <p className='mt-2 text-xs text-red-600'>Request ID: {requestId}</p>}
                </div>
              </div>
            </div>
            <div className='flex gap-3'>
              <button
                type='button'
                onClick={() => navigate('/transaction-history')}
                className='inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2'
              >
                Back to Transaction History
              </button>
              <button
                type='button'
                onClick={() => window.location.reload()}
                className='inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2'
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!requestId) return null;

  const receiveCurrency = transactionData?.receiveCurrency || tx?.quoteCurrency || 'EUR';
  const receiveAmount = transactionData?.receiveAmount ?? tx?.amountOut ?? null;
  const sendCurrency = transactionData?.sendCurrency || tx?.baseCurrency || 'EUR';
  const sendAmount = transactionData?.amount ?? tx?.amountIn ?? null;

  return (
    <Layout hideHeader showUserControls={false} mainPaddingClassName='px-6 py-9 sm:px-11' floatBack={{ onClick: handleBackHome, label: 'Back' }}>
      <div className='mx-auto flex w-full max-w-[680px] flex-col gap-[26px]'>
        {showProgressCard ? (
          <SendMoneyTransferProgressStep
            requestId={requestId}
            steps={stepsToDisplay}
            recipientName={recipientNameForGlobe}
            destinationCountry={destinationCountry}
            destinationCountryCode={destinationCountryCode}
            receiveAmount={receiveAmount}
            receiveCurrency={receiveCurrency}
            sendAmount={sendAmount}
            sendCurrency={sendCurrency}
            paymentReference={transactionData?.paymentReference || tx?.paymentReference || ''}
            paymentPurpose={transactionData?.paymentPurpose || tx?.paymentPurpose || ''}
            transferFee={transactionData?.transferFee || tx?.transferFee || null}
            exchangeRate={transactionData?.fxRate || tx?.exchangeRate || ''}
            paymentDocumentFileName={transactionData?.paymentDocumentFileName || ''}
            onBackHome={handleBackHome}
            trackMode
          />
        ) : (
          <div className='w-full rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-8 text-center shadow-[var(--shadow-md)]'>
            <p className='m-0 text-sm text-[var(--text-secondary)]'>Loading transfer status…</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TransactionProgress;
